import { Component, ViewChild, ElementRef, HostListener } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatDividerModule } from '@angular/material/divider';
import { BackgroundOperationViewModel } from 'src/app/background-operations/models/view-models/background-operation.view-model';
import { BackgroundOperationsListComponent } from "./background-operations-list/background-operations-list.component";
import { IconButtonComponent } from 'src/app/common/icon-button/icon-button.component';
import { BackgroundOperationsService } from 'src/app/background-operations/services/background-operations.service';
import { NavigationConstants } from 'src/app/common/navigation-constants';
import { Router } from '@angular/router';
import { MapLayoutService } from 'src/app/maps/layout/map-layout.service';
import { EsriBackgroundOperationViewModel } from 'src/app/maps/models/view-models/esri-background-operation.view-model';
import { BackgroundOperationType } from 'src/app/background-operations/models/enums/background-operation-type.enum';
import { ToastListComponent } from 'src/app/toast/toast-list/toast-list.component';
import { Observable } from 'rxjs';

/** Component for displaying background operations. */
@Component({
	standalone: true,
	selector: 'crownx-background-operations-drawer',
	imports: [
		NgIf,
		AsyncPipe,
		MatSidenavModule,
		MatDividerModule,
		BackgroundOperationsListComponent,
		IconButtonComponent
	],
	templateUrl: './background-operations-drawer.component.html',
	styleUrls: ['./background-operations-drawer.component.css']
})
export class BackgroundOperationsDrawerComponent {
	/** Reference to the outermost container element. */
	@ViewChild('BackgroundOperationsDrawerContainer')
	public BackgroundOperationsDrawerContainer!: ElementRef;

	/** Reference to the drawer component. */
	@ViewChild('BackgroundOperationsDrawer')
	public BackgroundOperationsDrawer!: MatSidenav;

	/** Observable stream of background operations */
	public readonly Operations$: Observable<BackgroundOperationViewModel[]> = this.backgroundOperationsService.Operations$;

	/**
	 * @constructor
	 * @param backgroundOperationsService Service for managing background operations.
	 * @param router Angular Router for navigation.
	 * @param mapLayoutService Service for managing map layout and interactions.
	 */
	constructor(
		private readonly backgroundOperationsService: BackgroundOperationsService,
		private readonly router: Router,
		private readonly mapLayoutService: MapLayoutService
	) { }

	/**
	 * Gets the count of operations currently in processing state.
	 * @param operations Array of background operations
	 * @returns The number of processing operations
	 */
	public GetProcessingCount(operations: BackgroundOperationViewModel[]): number {
		return operations.filter((operation: BackgroundOperationViewModel) => operation.IsProcessing).length;
	}

	/**
	 * Event handler for clicks outside of the sidenav.
	 * Closes the drawer if it is open and the click is outside the outer container.
	 * @param event The event associated with the click.
	 */
	@HostListener('document:mousedown', ['$event'])
	public OnGlobalClick(event: Event): void {
		const target: HTMLElement = event.target as HTMLElement;

		// Don't close if clicking on a toast element
		if (target.closest(`.${ToastListComponent.TOAST_LIST_CLASS}`) || target.closest(`.${ToastListComponent.TOAST_MESSAGE_CLASS}`)) {
			return;
		}
		
		if (this.BackgroundOperationsDrawer?.opened && !this.BackgroundOperationsDrawerContainer?.nativeElement.contains(event.target)) {
			this.BackgroundOperationsDrawer?.close();
		}
	}

	/**
	 * Handles the click event on a background operation.
	 * @param operation The background operation that was clicked
	 */
	public OnBackgroundOperationClick(operation: BackgroundOperationViewModel): void {
		this.BackgroundOperationsDrawer?.close();

		const isOnLiveMap: boolean = this.router.url.startsWith(NavigationConstants.LIVE_MAP_URL);

		// Navigate based on operation type
		if (operation instanceof EsriBackgroundOperationViewModel) {
			if (isOnLiveMap) {
				// Already on dispatch map, directly trigger route plan load to skip router navigation
				this.mapLayoutService.RequestLoadRoutePlan(operation.RoutePlanId);
			} else {
				// Navigate to dispatch map with route plan query param
				this.router.navigate([NavigationConstants.LIVE_MAP_URL], { queryParams: { routePlanId: operation.RoutePlanId } });
			}
		}
		else if (operation.Type === BackgroundOperationType.EsriCalculateAllEtas) {
			// Navigate to dispatch map only if not already there
			if (!isOnLiveMap) {
				this.router.navigate([NavigationConstants.LIVE_MAP_URL]);
			}
		}
	}
}